import React, {Component, ReactElement} from 'react'
import NatLoginBeforeMessageRepo, {
    SearchCondition,
    SearchConditionPageInfo,
} from '../loginBeforeMessage/repository/NatLoginBeforeMessageRepo'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {Nullable} from '../../../app/TypeHelpers'
import {SortOrder} from '../../../components/shared/SortOrder'
import {
    LoginBeforeMessageMasterInfo,
    NatGetLoginBeforeMessageList,
    SearchResult,
} from './repository/NatGetLoginBeforeMessageList'
import {COMMON_CONSTANTS} from '../../../CommonConstants'
import ListSearchFormArea from '../../../styledComponents/adminPage/ListSearchFormArea'
import {AdminDefaultMediumButton} from '../../../styledComponents/adminPage/Buttons'
import SearchPageActionArea from '../../../styledComponents/adminPage/SearchPageActionArea'
import {PageMainArea} from '../../../components/shared/hoc/withAdminPageTemplate'
import {SearchResultCountDisplayProps} from '../../../styledComponents/adminPage/SearchResultCountDisplay'
import SortableTableHeader, {
    SortableTableHeaderCell,
} from '../../../styledComponents/adminPage/SortablePaginationTableHeader'
import PPTextField from '../../../components/shared/forms/PPTextField'
import {SortDirection} from '@mui/material/TableCell'
import {Messages} from '../../../Messages'
import {
    formatDateTimeForUpdateDate,
    formatDateTimeWithoutSeconds,
    formatForDateInput,
} from '../../../formatters/formatDate'
import SortablePaginationTable from '../../../styledComponents/adminPage/SortablePaginationTable'
import SortablePaginationTableBody from '../../../styledComponents/adminPage/SortablePaginationTableBody'
import {HKConstants} from '../../../HKConstants'
import AuthorityChecker from '../../../components/AuthorityChecker'
import {
    ConditionButtonCell,
    ConditionCategoryCell,
    ConditionColumNameCell,
    ConditionInputCell,
    ConditionTable,
    ConditionTableBody,
    ConditionTableRow,
} from '../../../styledComponents/adminPage/ConditionTable'
import PPDatePicker from '../../../components/shared/forms/PPDatePicker'
import {StyledDiv, StyledSpan} from '../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {PUBLICATION_STATUS} from '../../../CodeMaster'
import {HeadingAreaMessageParams} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {LeftRowFlexContainer} from '../../../styledComponents/FlexComponents'
import {Location} from 'react-router-dom'

import {Link, TableCell, TableRow} from '@mui/material'

const KEY_SEARCH_CONDITION: string = 'adminLoginBeforeMessageListPageSearchCondition'

interface Props {
    loginBeforeMessageRepo: NatLoginBeforeMessageRepo
    eventEmitter: EventEmitterExAdmin
    location: Location

}

interface States {
    searchCondition: SearchCondition
    searchConditionPageInfo: SearchConditionPageInfo
    searchConditionSortInfo: SortOrder[]
    loginBeforeMessages: LoginBeforeMessageMasterInfo[]
    searchResult: SearchResult
    publishStartDatetimeMax: string
    publishEndDatetimeMin: string
    isLoadCompleted: boolean
}

interface LastSearchCondition extends SearchCondition {
    pageNum?: number
}

class NatAdminLoginBeforeMessageListPage extends Component<Props & AdminPagePropsSet, States> {

    subSortKey: SortOrder = new SortOrder('loginBeforeMessageId', 'asc')

    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        // 前回検索条件が保持されていればデフォルト値として設定する
        let lastSearchCondition: LastSearchCondition = {}
        const sessionStorageSearchCondition: string | null = window.sessionStorage.getItem(KEY_SEARCH_CONDITION)
        if (sessionStorageSearchCondition) {
            lastSearchCondition = JSON.parse(sessionStorageSearchCondition)
        }


        let defaultSearchCondition: SearchCondition = {}
        if (Object.keys(lastSearchCondition).length) {
            defaultSearchCondition = {
                title: lastSearchCondition.title,
                publishStartDatetime: lastSearchCondition.publishStartDatetime,
                publishEndDatetime: lastSearchCondition.publishEndDatetime,
            }
        }


        let defaultSearchConditionPage: SearchConditionPageInfo = {
            pageNum: COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[0],
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }
        if (lastSearchCondition.pageNum) {
            defaultSearchConditionPage = {
                pageNum: Number(lastSearchCondition.pageNum),
                page: COMMON_CONSTANTS.FIRST_PAGE,
            }
        }


        this.state = {
            searchCondition: defaultSearchCondition,
            searchConditionPageInfo: defaultSearchConditionPage,
            searchConditionSortInfo: [
                new SortOrder('publishStartDatetime', 'desc'),
            ],
            loginBeforeMessages: [],
            searchResult: {},
            publishStartDatetimeMax: '',
            publishEndDatetimeMin: '',
            isLoadCompleted: false,
        }

        this.changePublishStartDatetime = this.changePublishStartDatetime.bind(this)
        this.changePublishEndDatetime = this.changePublishEndDatetime.bind(this)
        this.onClickSearchButton = this.onClickSearchButton.bind(this)
    }

    async componentDidMount(): Promise<void> {

        await this.props.eventEmitter.showLoading()
        try {
            await this.searchLoginBeforeMessages(
                this.state.searchCondition,
                this.state.searchConditionPageInfo,
                this.state.searchConditionSortInfo,
            )
            this.showInitialMessage()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    async searchLoginBeforeMessages(
        searchCondition?: SearchCondition,
        searchConditionPageInfo?: SearchConditionPageInfo,
        searchConditionSortInfo?: SortOrder[],
    ): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {


            if (searchCondition) {
                if (searchCondition.publishStartDatetime) {
                    searchCondition.publishStartDatetime =
                        formatDateTimeForUpdateDate(searchCondition.publishStartDatetime)
                }
                if (searchCondition.publishEndDatetime) {
                    searchCondition.publishEndDatetime = formatDateTimeForUpdateDate(searchCondition.publishEndDatetime)
                }
            }

            // ソートのサブキーを追加
            let addSearchConditionSubSortInfo: SortOrder[] = []
            if (searchConditionSortInfo) {
                addSearchConditionSubSortInfo = searchConditionSortInfo.concat(this.subSortKey)
            } else {
                addSearchConditionSubSortInfo = addSearchConditionSubSortInfo.concat(this.subSortKey)
            }

            // ログイン前メッセージ一覧取得
            const getLoginBeforeMessageResponse: Nullable<NatGetLoginBeforeMessageList> =
                await this.props.asyncCallFunction(
                    this.props.loginBeforeMessageRepo.getLoginBeforeMessageMasterList.bind(
                        this.props.loginBeforeMessageRepo,
                        searchCondition,
                        searchConditionPageInfo,
                        addSearchConditionSubSortInfo,
                    ),
                    (httpErrorOrNetworkError) => {
                        this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )

            if (getLoginBeforeMessageResponse) {
                if (getLoginBeforeMessageResponse.details) {
                    this.setState({
                        loginBeforeMessages: getLoginBeforeMessageResponse.details.loginBeforeMessageMasterInfo,
                        searchResult: getLoginBeforeMessageResponse.details.searchResult,
                    })
                }

            }

        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        this.beforeUserAction()

        // 検索条件を保持
        const lastSearchCondition: LastSearchCondition = {
            title: this.state.searchCondition.title,
            publishStartDatetime: this.state.searchCondition.publishStartDatetime,
            publishEndDatetime: this.state.searchCondition.publishEndDatetime,
            pageNum: Number(event.target.value),

        }
        SessionStorageHelper.setItem(KEY_SEARCH_CONDITION, JSON.stringify(lastSearchCondition))

        const searchConditionPageInfo: SearchConditionPageInfo = this.state.searchConditionPageInfo
        searchConditionPageInfo.pageNum = Number(event.target.value)
        searchConditionPageInfo.page = COMMON_CONSTANTS.FIRST_PAGE
        await this.searchLoginBeforeMessages(
            this.state.searchCondition,
            searchConditionPageInfo,
            this.state.searchConditionSortInfo,
        )
        this.setState({searchConditionPageInfo: searchConditionPageInfo})
    }

    beforeUserAction(): void {
        this.props.eventEmitter.clearHeadingAreaMessage()
    }

    async handleSortLoginBeforeMessages(sortKey: string): Promise<void> {
        this.beforeUserAction()

        let sortDirection: SortDirection = this.getSortDirection(sortKey)
        if (sortDirection === false || sortDirection === 'desc') {
            sortDirection = 'asc'
        } else {
            sortDirection = 'desc'
        }

        const sortOrders: SortOrder[] = [new SortOrder(sortKey, sortDirection)]
        await this.searchLoginBeforeMessages(
            this.state.searchCondition,
            this.state.searchConditionPageInfo,
            sortOrders,
        )
        this.setState({searchConditionSortInfo: sortOrders})
    }


    async handleChangePage(
        event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
        newPage: number,
    ): Promise<void> {
        this.beforeUserAction()
        const searchConditionPageInfo: SearchConditionPageInfo = this.state.searchConditionPageInfo
        searchConditionPageInfo.page = newPage
        await this.searchLoginBeforeMessages(
            this.state.searchCondition,
            searchConditionPageInfo,
            this.state.searchConditionSortInfo,
        )
        this.setState({searchConditionPageInfo: searchConditionPageInfo})
    }


    renderLoginBeforeMessageTable(loginBeforeMessages: LoginBeforeMessageMasterInfo[]): Nullable<ReactElement> {
        if (loginBeforeMessages.length === 0) {
            return null
        }
        const headers: SortableTableHeaderCell[] = [
            {id: 'title', label: 'タイトル'},
            {
                id: 'publishStartDatetime',
                label: '掲載開始日時',
                sortable: true,
                direction: this.getSortDirection('publishStartDatetime'),
            },
            {
                id: 'publishEndDatetime',
                label: '掲載終了日時',
                sortable: true,
                direction: this.getSortDirection('publishEndDatetime'),
            },
            {id: 'status', label: '掲載状態'},
            {
                id: 'updateDatetime',
                label: '更新日時',
                sortable: true,
                direction: this.getSortDirection('updateDatetime'),
            },
        ]

        return (
            <SortablePaginationTable
                paginationProps={{
                    count: this.state.searchResult.total ? this.state.searchResult.total : 0,
                    rowsPerPage:
                        this.state.searchConditionPageInfo.pageNum ? this.state.searchConditionPageInfo.pageNum : 0,
                    page: this.state.searchConditionPageInfo.page ? this.state.searchConditionPageInfo.page : 0,
                    onPageChange: this.handleChangePage.bind(this),
                    onRowsPerPageChange: this.handleChangeRowsPerPage.bind(this),
                }}
            >
                <SortableTableHeader
                    noWrap
                    headers={headers}
                    onSortOrderChange={this.handleSortLoginBeforeMessages.bind(this)}
                />
                <SortablePaginationTableBody>
                    {this.state.loginBeforeMessages.map(
                        loginBeforeMessage => this.renderLoginBeforeMessageRecord(loginBeforeMessage),
                    )}
                </SortablePaginationTableBody>
            </SortablePaginationTable>
        )
    }


    renderLoginBeforeMessageRecord(loginBeforeMessage: LoginBeforeMessageMasterInfo): ReactElement {
        const loginBeforeMessageUrl: string = `/admin/loginBeforeMessage/${loginBeforeMessage.loginBeforeMessageId}`
        return (
            <TableRow key={loginBeforeMessage.loginBeforeMessageId}>
                <TableCell align={'center'}><Link href={loginBeforeMessageUrl}
                                                  underline="hover">{loginBeforeMessage.title}</Link></TableCell>
                <TableCell align={'center'}>
                    {formatDateTimeWithoutSeconds(loginBeforeMessage.publishStartDatetime)}
                </TableCell>
                <TableCell align={'center'}>
                    {formatDateTimeWithoutSeconds(loginBeforeMessage.publishEndDatetime)}
                </TableCell>
                <TableCell align={'center'}>{this.getDispPublishStatus(loginBeforeMessage.publishStatus)}</TableCell>
                <TableCell align={'center'}>{formatDateTimeForUpdateDate(loginBeforeMessage.updateDatetime)}</TableCell>
            </TableRow>
        )
    }

    getDispPublishStatus(publishStatus: string): string {
        const publicationStatusCode = PUBLICATION_STATUS.codeToEnumConstants(publishStatus)
        if (publicationStatusCode) {
            return publicationStatusCode.label
        } else {
            return ''
        }
    }


    getSortDirection(sortKey: string): SortDirection {
        const sortOrder: SortOrder | undefined =
            this.state.searchConditionSortInfo.find(order => order.sortKey === sortKey)
        if (sortOrder === undefined) {
            return false
        }
        return sortOrder.sortDirection
    }


    handleNewLoginBeforeMessageButtonClick(): void {
        this.props.navigate(
            `/admin/loginBeforeMessageRegister/`,
        )
    }

    onChangeTitle(event: React.ChangeEvent<HTMLInputElement>): void {
        const seachConditions = this.state.searchCondition
        seachConditions.title = event.target.value
        this.setState({searchCondition: seachConditions})
    }

    searchResults(): SearchResultCountDisplayProps {
        if (this.state.loginBeforeMessages.length === 0) {
            if (!Object.keys(this.state.searchCondition).length) {
                return {noResultMessage: Messages.IOPCC0015.toString('ログイン前メッセージ')}
            }
            return {noResultMessage: Messages.IOPCC0014.toString('')}
        }

        return {
            totalCount: this.state.searchResult.total ? this.state.searchResult.total : undefined,
            rowsPerPage: this.state.searchConditionPageInfo.pageNum,
            page: this.state.searchConditionPageInfo.page,
        }
    }

    changePublishStartDatetime(date: Nullable<Date>): void {
        const seachConditions = this.state.searchCondition
        if (date !== null) {
            seachConditions.publishStartDatetime = formatForDateInput(date.toString())
        } else {
            seachConditions.publishStartDatetime = ''
        }

        this.setState({
            searchCondition: seachConditions,
            publishEndDatetimeMin: seachConditions.publishStartDatetime,
        })

    }

    changePublishEndDatetime(date: Nullable<Date>): void {
        const seachConditions = this.state.searchCondition
        if (date !== null) {
            seachConditions.publishEndDatetime = formatForDateInput(date.toString())

        } else {
            seachConditions.publishEndDatetime = ''
        }

        this.setState({
            searchCondition: seachConditions,
            publishStartDatetimeMax: seachConditions.publishEndDatetime,
        })
    }

    async onClickSearchButton(): Promise<void> {
        this.beforeUserAction()
        // 検索条件を保持
        const lastSearchCondition: LastSearchCondition = {
            title: this.state.searchCondition.title,
            publishStartDatetime: this.state.searchCondition.publishStartDatetime,
            publishEndDatetime: this.state.searchCondition.publishEndDatetime,
            pageNum: this.state.searchConditionPageInfo.pageNum,

        }
        SessionStorageHelper.setItem(KEY_SEARCH_CONDITION, JSON.stringify(lastSearchCondition))

        const searchConditionPageInfo: SearchConditionPageInfo = {
            pageNum: this.state.searchConditionPageInfo.pageNum,
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }
        await this.searchLoginBeforeMessages(
            this.state.searchCondition,
            searchConditionPageInfo,
            this.state.searchConditionSortInfo)
        this.setState({searchConditionPageInfo: searchConditionPageInfo})
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return (
            <>
                <ListSearchFormArea
                    searchResult={this.searchResults()}
                    onEnterKey={this.onClickSearchButton.bind(this)}
                >

                    <ConditionTable>
                        <ConditionTableBody>
                            <ConditionTableRow>
                                <ConditionCategoryCell rowSpan={2}>ログイン前メッセージ情報</ConditionCategoryCell>
                                <ConditionColumNameCell>タイトル</ConditionColumNameCell>
                                <ConditionInputCell>
                                    <PPTextField
                                        InputProps={{
                                            inputProps: {
                                                maxLength: 50,
                                            },
                                        }}
                                        fullWidth
                                        onChange={this.onChangeTitle.bind(this)}
                                        defaultValue={this.state.searchCondition.title}
                                        autoFocus
                                    />
                                </ConditionInputCell>
                            </ConditionTableRow>
                            <ConditionTableRow>
                                <ConditionColumNameCell>掲載期間</ConditionColumNameCell>
                                <ConditionInputCell>
                                    <LeftRowFlexContainer>
                                        <TermWrapper>
                                            <FlexBasis>
                                                <PPDatePicker
                                                    onChangeDate={this.changePublishStartDatetime}
                                                    value={(this.state.searchCondition.publishStartDatetime &&
                                                        this.state.searchCondition.publishStartDatetime !== '') ?
                                                        new Date(this.state.searchCondition.publishStartDatetime) : null
                                                    }
                                                    eventEmitter={this.props.eventEmitter}
                                                    eventKeys={['date']}
                                                    maxDate={(this.state.publishStartDatetimeMax !== '') ?
                                                        new Date(this.state.publishStartDatetimeMax) : undefined
                                                    }
                                                />
                                            </FlexBasis>
                                            <TermTxt>から</TermTxt>
                                        </TermWrapper>
                                        <TermWrapper>
                                            <FlexBasis>
                                                <PPDatePicker
                                                    onChangeDate={this.changePublishEndDatetime}
                                                    value={(this.state.searchCondition.publishEndDatetime &&
                                                        this.state.searchCondition.publishEndDatetime !== '') ?
                                                        new Date(this.state.searchCondition.publishEndDatetime) : null
                                                    }
                                                    eventEmitter={this.props.eventEmitter}
                                                    eventKeys={['date']}
                                                    minDate={(this.state.publishEndDatetimeMin !== '') ?
                                                        new Date(this.state.publishEndDatetimeMin) : undefined
                                                    }
                                                />
                                            </FlexBasis>
                                            <TermTxt>の間</TermTxt>
                                        </TermWrapper>
                                    </LeftRowFlexContainer>
                                </ConditionInputCell>
                                <ConditionButtonCell>
                                    <AdminDefaultMediumButton
                                        onClick={this.onClickSearchButton}>
                                        ログイン前メッセージ<br/>を検索する
                                    </AdminDefaultMediumButton>
                                </ConditionButtonCell>
                            </ConditionTableRow>
                        </ConditionTableBody>
                    </ConditionTable>
                </ListSearchFormArea>
                <SearchPageActionArea>
                    <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_LOGIN_BEFORE_MESSAGE}>
                        <AdminDefaultMediumButton onClick={this.handleNewLoginBeforeMessageButtonClick.bind(this)}>
                            新規
                        </AdminDefaultMediumButton>
                    </AuthorityChecker>
                </SearchPageActionArea>
                <PageMainArea>
                    {this.renderLoginBeforeMessageTable(this.state.loginBeforeMessages)}
                </PageMainArea>
            </>
        )
    }
}

export default createAdminPage(NatAdminLoginBeforeMessageListPage)

const TermWrapper: StyledDiv = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: flex-end;
`

const TermTxt: StyledSpan = styled.span`
  padding: 0 5px 0 5px;
`

const FlexBasis: StyledDiv = styled.div`
  flex: 1 1 0%;
`
